import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Key, Plus, Trash2, Copy, Check, Eye, EyeOff, Loader2, Code } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ApiKeysPage = () => {
  const [loading, setLoading] = useState(true);
  const [keys, setKeys] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [keyForm, setKeyForm] = useState({
    name: "",
    permissions: ["read"]
  });

  useEffect(() => {
    loadKeys();
  }, []);

  const loadKeys = async () => {
    try {
      const token = localStorage.getItem("cia_token");
      const response = await axios.get(`${API}/api-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKeys(response.data);
    } catch (error) {
      if (error.response?.status === 400) {
        // Plan doesn't support API keys
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const token = localStorage.getItem("cia_token");
      const response = await axios.post(`${API}/api-keys`, keyForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewKeyResult(response.data);
      loadKeys();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear API key");
      setShowCreateDialog(false);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId) => {
    if (!window.confirm("¿Estás seguro de revocar esta API key? Esta acción no se puede deshacer.")) return;
    
    try {
      const token = localStorage.getItem("cia_token");
      await axios.delete(`${API}/api-keys/${keyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("API key revocada");
      loadKeys();
    } catch (error) {
      toast.error("Error al revocar API key");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copiado al portapapeles");
    setTimeout(() => setCopied(false), 2000);
  };

  const togglePermission = (perm) => {
    const current = keyForm.permissions;
    if (current.includes(perm)) {
      setKeyForm({ ...keyForm, permissions: current.filter(p => p !== perm) });
    } else {
      setKeyForm({ ...keyForm, permissions: [...current, perm] });
    }
  };

  const closeAndReset = () => {
    setShowCreateDialog(false);
    setNewKeyResult(null);
    setKeyForm({ name: "", permissions: ["read"] });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#5B9BD5]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="api-keys-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">API Keys</h1>
          <p className="text-slate-600">Integra CIA Factura℗ con tus sistemas externos</p>
        </div>
        
        <Dialog open={showCreateDialog} onOpenChange={(open) => {
          if (!open) closeAndReset();
          else setShowCreateDialog(true);
        }}>
          <DialogTrigger asChild>
            <Button className="cia-btn-primary" data-testid="create-api-key-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nueva API Key
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {newKeyResult ? "¡API Key Creada!" : "Crear Nueva API Key"}
              </DialogTitle>
            </DialogHeader>
            
            {newKeyResult ? (
              <div className="space-y-4">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-800 text-sm font-medium mb-2">
                    Guarda esta key ahora. No podrás verla de nuevo.
                  </p>
                </div>
                
                <div className="p-4 bg-slate-100 rounded-lg">
                  <Label className="text-xs text-slate-500">Tu API Key</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 text-sm font-mono bg-white p-2 rounded border">
                      {newKeyResult.key_preview}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(newKeyResult.key_preview)}
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                
                <Button onClick={closeAndReset} className="w-full">
                  Entendido
                </Button>
              </div>
            ) : (
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nombre de la key</Label>
                  <Input
                    value={keyForm.name}
                    onChange={(e) => setKeyForm({ ...keyForm, name: e.target.value })}
                    placeholder="Ej: Integración ERP"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Permisos</Label>
                  <div className="space-y-2">
                    {[
                      { id: "read", label: "Lectura", desc: "Ver facturas y clientes" },
                      { id: "write", label: "Escritura", desc: "Crear facturas y clientes" },
                      { id: "delete", label: "Eliminar", desc: "Cancelar facturas" }
                    ].map(perm => (
                      <label key={perm.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                        <input
                          type="checkbox"
                          checked={keyForm.permissions.includes(perm.id)}
                          onChange={() => togglePermission(perm.id)}
                          className="w-4 h-4"
                        />
                        <div>
                          <p className="font-medium">{perm.label}</p>
                          <p className="text-xs text-slate-500">{perm.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                
                <Button type="submit" className="w-full cia-btn-primary" disabled={creating}>
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear API Key"}
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* API Keys List */}
      <Card className="cia-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5 text-[#5B9BD5]" />
            Tus API Keys ({keys.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {keys.length === 0 ? (
            <div className="text-center py-8">
              <Key className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No tienes API keys</p>
              <p className="text-sm text-slate-400">Crea una para integrar con sistemas externos</p>
            </div>
          ) : (
            <div className="space-y-3">
              {keys.map((key) => (
                <div key={key.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#5B9BD5]/10 flex items-center justify-center">
                      <Key className="w-5 h-5 text-[#5B9BD5]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{key.name}</p>
                      <p className="text-sm text-slate-500 font-mono">{key.key_preview}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right text-sm">
                      <p className="text-slate-600">{key.permissions?.join(", ")}</p>
                      <p className="text-xs text-slate-400">
                        {key.last_used ? `Último uso: ${new Date(key.last_used).toLocaleDateString()}` : "Nunca usado"}
                      </p>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRevoke(key.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* API Documentation */}
      <Card className="cia-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="w-5 h-5 text-[#5B9BD5]" />
            Documentación API
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-slate-600">Usa tu API key en el header de cada petición:</p>
          
          <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
            <code className="text-green-400 text-sm">
              curl -X GET "{process.env.REACT_APP_BACKEND_URL}/api/public/invoices" \<br/>
              &nbsp;&nbsp;-H "X-API-Key: cia_tu_api_key_aqui"
            </code>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-semibold text-slate-900 mb-2">Endpoints Disponibles</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li><code className="text-xs bg-slate-200 px-1 rounded">GET</code> /api/public/invoices</li>
                <li><code className="text-xs bg-slate-200 px-1 rounded">GET</code> /api/public/clients</li>
                <li><code className="text-xs bg-blue-200 px-1 rounded">POST</code> /api/public/invoices</li>
              </ul>
            </div>
            
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-semibold text-slate-900 mb-2">Rate Limits</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>Básico: No disponible</li>
                <li>Profesional: No disponible</li>
                <li>Empresarial: Sin límite</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ApiKeysPage;
